import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import Customer, {
  CustomerShippingAccount,
} from "../../models/Customer";
import { AppContext } from "../../AppContextProvider";
import { createCustomer, updateCustomer } from "../../api/ProcurementClient";
import { AxiosError } from "axios";

interface DialogProps {
  customer?: Customer;
  customerName?: string;
  onClose: (customer?: Customer) => void;
  saveOnClose: boolean;
}

const emptyAccount = (): CustomerShippingAccount => ({
  label: "",
  accountNumber: "",
});

const CustomerDialog: React.FC<DialogProps> = ({
  customer,
  onClose,
  saveOnClose,
  customerName,
}) => {
  const { customers, setCustomers, setSnackbarErrorMsg, setSnackbarMsg } =
    useContext(AppContext);

  const [name, setName] = useState(customer?.name ?? customerName ?? "");
  const initialShippingAccounts = customer?.shippingAccounts;
  const [shippingAccounts, setShippingAccounts] = useState<
    CustomerShippingAccount[]
  >(Array.isArray(initialShippingAccounts) ? initialShippingAccounts : []);

  const nameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setName(customer?.name ?? customerName ?? "");
    const accounts = customer?.shippingAccounts;
    setShippingAccounts(Array.isArray(accounts) ? accounts : []);
  }, [customer, customerName]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (nameInputRef.current) {
        nameInputRef.current.focus();
      }
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  const normalizedAccounts = shippingAccounts
    .map((a) => ({
      label: a.label.trim(),
      accountNumber: a.accountNumber.trim(),
    }))
    .filter((a) => a.label || a.accountNumber);

  const isSaveDisabled =
    !name.length ||
    normalizedAccounts.some((a) => !a.label || !a.accountNumber);

  const updateAccount = (
    index: number,
    field: keyof CustomerShippingAccount,
    value: string
  ) => {
    setShippingAccounts((prev) =>
      prev.map((account, i) =>
        i === index ? { ...account, [field]: value } : account
      )
    );
  };

  const handleOk = async () => {
    if (saveOnClose) {
      if (!customer) {
        try {
          const newCustomer = await createCustomer({
            id: 0,
            name,
            shipAcctType: "",
            shipAcctNum: "",
            contacts: [],
            qcCodes: [],
            shippingAccounts: normalizedAccounts,
          });
          setCustomers(customers.concat(newCustomer));
          setSnackbarMsg("Client created");
          onClose(newCustomer);
        } catch (error) {
          const axiosError = error as AxiosError;
          setSnackbarErrorMsg(
            "Error creating client: " +
              (axiosError.response?.data?.toString() ??
                axiosError.response?.statusText ??
                "Unknown server error")
          );
        }
        return;
      }

      const updatedCustomer: Customer = {
        ...customer,
        name,
        shippingAccounts: normalizedAccounts,
      };

      try {
        const savedCustomer = await updateCustomer(updatedCustomer);
        setCustomers(
          customers.map((c) => (c.id === savedCustomer.id ? savedCustomer : c))
        );
        setSnackbarMsg("Client updated");
        onClose();
      } catch (error) {
        const axiosError = error as AxiosError;
        setSnackbarErrorMsg(
          "Error updating client: " +
            (axiosError.response?.data?.toString() ??
              axiosError.response?.statusText ??
              "Unknown server error")
        );
      }
    } else {
      onClose(
        !customer
          ? {
              id: 0,
              name,
              shipAcctType: "",
              shipAcctNum: "",
              contacts: [],
              qcCodes: [],
              shippingAccounts: normalizedAccounts,
            }
          : { ...customer, name, shippingAccounts: normalizedAccounts }
      );
    }
  };

  return (
    <Dialog
      open={true}
      onClose={() => onClose()}
      fullWidth={true}
      maxWidth="sm"
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      disableRestoreFocus={true}
    >
      <DialogTitle>
        {!customer ? "New Client" : "Edit Client: " + customer.name}
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            pt: 1,
            display: "grid",
            gridTemplateColumns: "1fr",
            gap: 2,
          }}
        >
          <TextField
            fullWidth
            required={true}
            label="Name"
            variant="standard"
            value={name}
            inputRef={nameInputRef}
            autoFocus
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setName(event.target.value)
            }
          />
          <Stack gap={1}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="subtitle2">Shipping Accounts</Typography>
              <IconButton
                size="small"
                color="primary"
                aria-label="Add shipping account"
                onClick={() =>
                  setShippingAccounts((prev) => [...prev, emptyAccount()])
                }
              >
                <Add fontSize="small" />
              </IconButton>
            </Stack>
            {shippingAccounts.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No shipping accounts. Use + to add FedEx/UPS/etc. numbers.
              </Typography>
            ) : (
              shippingAccounts.map((account, index) => (
                <Stack
                  key={index}
                  direction="row"
                  gap={1}
                  alignItems="flex-start"
                >
                  <TextField
                    label="Label"
                    variant="standard"
                    value={account.label}
                    placeholder="Fed-Ex Account #"
                    fullWidth
                    onChange={(e) =>
                      updateAccount(index, "label", e.target.value)
                    }
                  />
                  <TextField
                    label="Account #"
                    variant="standard"
                    value={account.accountNumber}
                    fullWidth
                    onChange={(e) =>
                      updateAccount(index, "accountNumber", e.target.value)
                    }
                  />
                  <IconButton
                    size="small"
                    color="error"
                    aria-label="Remove shipping account"
                    onClick={() =>
                      setShippingAccounts((prev) =>
                        prev.filter((_, i) => i !== index)
                      )
                    }
                    sx={{ mt: 1 }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Stack>
              ))
            )}
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={() => onClose()}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleOk}
          disabled={isSaveDisabled}
        >
          {!saveOnClose ? "OK" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomerDialog;
